/**
 * parent/index.tsx — Parent dashboard landing (N-17).
 *
 * Lists the parent's approved children with quick links to each
 * child's read-only progress view. The list is sourced from
 * `myChildrenQuery` and gated by the parent role check in `beforeLoad`.
 *
 * Empty state surfaces two scenarios:
 *   - No approved children → "Klaim akses dulu" CTA pointing to /parent/onboarding.
 *   - Admin viewing as parent → empty state instead of claim CTA.
 */

import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { StatePanel } from '../../components/ui/StatePanel.js';
import { freshSessionQuery } from '../../lib/api/queries.js';
import { myChildrenQuery } from '../../lib/api/queries.js';

export const Route = createFileRoute('/parent/')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role !== 'parent' && user.role !== 'admin') {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: ParentDashboardPage,
});

function ParentDashboardPage(): ReactNode {
  const children = useQuery(myChildrenQuery);
  const list = children.data?.ok ? children.data.data.children : [];

  return (
    <main className="page-shell" data-testid="parent-dashboard">
      <PageHeader
        eyebrow="Dashboard orang tua"
        title="Anak Anda"
        description="Akses rapor progres belajar anak Anda di Funpaper Coding."
      />

      {children.isLoading ? (
        <p>Memuat daftar anak…</p>
      ) : list.length === 0 ? (
        <StatePanel
          variant="empty"
          title="Belum ada anak terdaftar"
          description="Jika Anda baru diundang, klik link di email untuk mengklaim akses. Atau minta admin sekolah untuk mengundang Anda."
          action={
            <Link to="/parent/onboarding" className="button button-primary">
              Buka halaman onboarding
            </Link>
          }
        />
      ) : (
        <ul className="parent-children-list" data-testid="parent-children-list">
          {list.map((child) => (
            <li
              key={child.studentId}
              className="parent-child-card"
              data-testid={`parent-child-${child.studentId}`}
            >
              <Link
                to="/parent/children/$studentId"
                params={{ studentId: child.studentId }}
                className="parent-child-card-link"
              >
                <h3 className="card-title">{child.name}</h3>
                <p className="card-meta">
                  {child.sdGrade ? `SD Kelas ${child.sdGrade}` : 'Siswa'}
                  {child.className ? ` · ${child.className}` : ''}
                </p>
                {child.email ? (
                  <p className="form-message form-message-muted">{child.email}</p>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}